import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App.js";
import { configureAuth } from "./auth/amplify.js";
import { AuthProvider } from "./auth/AuthProvider.js";
import { ErrorBoundary } from "./ui/ErrorBoundary.js";
import { OfflineBanner } from "./ui/OfflineBanner.js";
// Imported here (not in App) so component tests never load the
// virtual:pwa-register module, which only exists under the Vite plugin.
import { ReloadPrompt } from "./ui/ReloadPrompt.js";
// Brand typefaces (Brand & Style Guide, Aug 2026): Gloock display,
// Big Shoulders labels, Work Sans body.
import "@fontsource/gloock/400.css";
import "@fontsource/big-shoulders/700.css";
import "@fontsource/work-sans/400.css";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import "@fontsource/work-sans/700.css";
import "./index.css";

configureAuth();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element missing from index.html");

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <OfflineBanner />
            <App />
            <ReloadPrompt />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
